import React, { useState } from 'react';
import { Modal, Table, Button, Flex, Alert, Typography } from 'antd';
import Icon, { InfoCircleOutlined, UndoOutlined } from '@ant-design/icons';
import { mdiMouseLeftClickOutline } from '@mdi/js';

import type { ColumnsType } from 'antd/es/table';
import type { RadioChangeEvent } from 'antd';

const { Paragraph, Text } = Typography;

interface HelpModalProps {
    open: boolean;
    onClose: () => void;
}

interface Shortcut {
    id: string;
    key: React.ReactNode;
    description: string;
}

const TIPS = [
    'Если начнете поиск со /, то он будет работать как регулярное выражение',
    'Наведите курсор на автар, чтобы увидеть его подробную информацию',
    'Правый клик по эпику: привязать задачу Яндекс Трекера, HTML-отчёт или открыть их',
    'Редактор поддерживает смайлки, поробуйте написать <3 или :)',
    'Благодаря авторизации теперь можно пользоваться с VPN',
    'Для удаления ссылки нажмите на нее правой кнопкой мыши',
    'Если нажать правой кнопкой мыши на задачу, то можно открыть историю',
    'Alt + клик выделяет несколько задач — их можно перетащить все сразу',
    'Двойной клик по шапке столбца выделяет или снимает выделение со всех задач в нём',
];

const MdiMouseLeftClickOutlineIcon = () => (
    <svg width="1em" height="1em" fill="currentColor" viewBox="0 0 24 24">
        <path d={mdiMouseLeftClickOutline} />
    </svg>
);

const HelpModal: React.FC<HelpModalProps> = ({ open, onClose }) => {
    const [currentTip, setCurrentTip] = useState(TIPS[0]);

    const globalShortcuts: Shortcut[] = [
        { id: 'ctrl-o', key: 'Ctrl + O', description: 'Добавить задачу или эпик' },
        { id: 'ctrl-f', key: 'Ctrl + F', description: 'Нечеткий поиск по задачам' },
        { id: 'ctrl-e', key: 'Ctrl + E, Ctrl + B', description: 'Переключить меню' },
        {
            id: 'click-edit',
            key: (
                <Flex align="center">
                     <Icon
                        style={{ fontSize: 20, marginRight: 6 }}
                        component={MdiMouseLeftClickOutlineIcon}
                    />
                </Flex>
            ),
            description: 'Редактировать задачу'
        },
        {
            id: 'alt-click-select',
            key: (
                <Flex align="center" gap={6}>
                    Alt +
                    <Icon
                        style={{ fontSize: 20 }}
                        component={MdiMouseLeftClickOutlineIcon}
                    />
                </Flex>
            ),
            description: 'Выделить задачу. Перетащите выделенные, чтобы перенести все сразу'
        },
        {
            id: 'header-dblclick-select',
            key: (
                <Flex align="center" gap={6}>
                    Двойной клик по шапке
                    <Icon
                        style={{ fontSize: 20 }}
                        component={MdiMouseLeftClickOutlineIcon}
                    />
                </Flex>
            ),
            description: 'Выделить или снять выделение со всех задач в столбце'
        },
        { id: 'esc-clear', key: 'Esc', description: 'Снять выделение с задач' },
    ];

    const editorShortcuts: Shortcut[] = [
        { id: 'ctrl-z', key: 'Ctrl + Z', description: 'Отменить изменения' },
        { id: 'ctrl-y', key: 'Ctrl + Y', description: 'Вернуть изменения' },
        { id: 'ctrl-b', key: 'Ctrl + B', description: 'Выделить жирным' },
        { id: 'ctrl-i', key: 'Ctrl + I', description: 'Выделить курсивом' },
        { id: 'ctrl-enter', key: 'Ctrl + Enter', description: 'Сохранить и закрыть' },
        { id: 'ctrl-mono', key: 'Ctrl + E', description: 'Выделить моношрифтом' },
    ];

    const handleTipChange = () => {
        setCurrentTip(TIPS[Math.floor(Math.random() * TIPS.length)]);
    };

    const columns: ColumnsType<Shortcut> = [
        {
            title: 'Сочетание клавиш',
            dataIndex: 'key',
            key: 'key',
            width: '30%',
            render: (text) => text,
        },
        {
            title: 'Описание',
            dataIndex: 'description',
            key: 'description',
        },
    ];

    return (
        <Modal
            open={open}
            onCancel={onClose}
            footer={null}
            width={600}
        >
            <div style={{ maxHeight: '80vh', overflow: 'auto' }}>
                <Alert
                    message={
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                            <span style={{ flex: 1 }}>{currentTip}</span>
                            <Button
                                type="text"
                                icon={<UndoOutlined />}
                                onClick={handleTipChange}
                                title="Сменить подсказку"
                            />
                        </div>
                    }
                    type="info"
                    style={{ margin: '30px 0 15px 0' }}
                />

                <div style={{ marginBottom: 30 }}>
                    <Paragraph strong>
                        Глобальные сочетания клавиш
                    </Paragraph>

                    <Table
                        dataSource={globalShortcuts}
                        columns={columns}
                        pagination={false}
                        rowKey="id"
                        bordered
                        showHeader={false}
                    />
                </div>
                <div>
                    <Paragraph strong>
                        Сочетания клавиш в редакторе
                    </Paragraph>
                    <Table
                        dataSource={editorShortcuts}
                        columns={columns}
                        pagination={false}
                        rowKey="id"
                        bordered
                        showHeader={false}
                    />
                </div>
            </div>
        </Modal>
    );
};

export default HelpModal;
